import { useCallback, useEffect } from 'react';

import { type EchoSubmitEndDetail } from '@betterment/js-runtime';
import Cookies from 'js-cookie';
import { useLocation } from 'react-router-dom';
import * as yup from 'yup';

import { useNotifications } from './useNotifications';

const payloadSchema = yup.object().shape({
  _rails: yup.object().shape({
    exp: yup.string().nullable(),
    pur: yup.string().required(),
    message: yup.string().required(),
  }),
});

const toastSchema = yup.object().shape({
  success: yup.string(),
  error: yup.string(),
  warning: yup.string(),
  info: yup.string(),
});

export type Toast = yup.Asserts<typeof toastSchema>;

function parseToastCookie(cookie: string): Toast {
  const [data, _signature] = cookie.split('--', 2);
  const { _rails } = payloadSchema.validateSync(JSON.parse(atob(data!)));
  return toastSchema.validateSync(JSON.parse(atob(_rails.message)));
}

export function readToastCookie(): Toast | null {
  const cookie = Cookies.get('toast');
  if (!cookie) return null;

  Cookies.remove('toast');

  try {
    return parseToastCookie(cookie);
  } catch (error) {
    if (error instanceof yup.ValidationError) {
      throw error;
    }

    return null;
  }
}

export function useToastCookie() {
  const location = useLocation();
  const { showNotification } = useNotifications();

  const renderToastCookie = useCallback(() => {
    const toast = readToastCookie();

    if (toast?.success) showNotification({ type: 'success', message: toast.success });
    if (toast?.error) showNotification({ type: 'critical', message: toast.error });
    if (toast?.warning) showNotification({ type: 'warning', message: toast.warning });
    if (toast?.info) showNotification({ type: 'info', message: toast.info });
  }, [showNotification]);

  /**
   * Whenever you land on a new page, check for toast messages.
   */
  useEffect(() => renderToastCookie(), [location, renderToastCookie]);

  /**
   * If a form submission does not redirect, check for toast messages.
   */
  useEffect(() => {
    const onSubmitEnd = (event: CustomEvent<EchoSubmitEndDetail>) => {
      if (event.detail.status !== 204) renderToastCookie();
    };

    document.addEventListener('echo:submit-end', onSubmitEnd as EventListener);
    return () => document.removeEventListener('echo:submit-end', onSubmitEnd as EventListener);
  }, [renderToastCookie]);
}
